import html2canvas from "html2canvas";
import { normalizeReportColors } from "./normalizeReportColors";

// Capture in a document owned by this export. Removing its outer frame also
// removes html2canvas's internal frames, including ones left behind on errors.
export async function captureReportPages(source, onPage, signal) {
  if (!source?.children.length || signal.aborted) return false;

  const ownerDocument = source.ownerDocument;
  const frame = ownerDocument.createElement("iframe");
  frame.title = "Report PDF capture";
  frame.setAttribute("aria-hidden", "true");
  frame.setAttribute("data-html2canvas-ignore", "true");
  Object.assign(frame.style, {
    position: "fixed",
    left: "-100000px",
    top: "0",
    width: `${ownerDocument.defaultView.innerWidth}px`,
    height: `${ownerDocument.defaultView.innerHeight}px`,
    border: "0",
    pointerEvents: "none",
  });

  let finishStyles;
  const cancelled = new Promise((resolve) => {
    finishStyles = resolve;
  });
  const dispose = () => {
    frame.remove();
    finishStyles();
  };
  signal.addEventListener("abort", dispose, { once: true });

  try {
    ownerDocument.body.appendChild(frame);
    const captureDocument = frame.contentDocument;
    const captureWindow = frame.contentWindow;
    captureDocument.open();
    captureDocument.write(
      "<!doctype html><html><head></head><body></body></html>"
    );
    captureDocument.close();

    // Preserve theme, responsive layout and ancestor selectors without copying
    // unrelated pages, drawers or previous exports into every canvas capture.
    ["documentElement", "body"].forEach((key) => {
      Array.from(ownerDocument[key].attributes).forEach(({ name, value }) => {
        captureDocument[key].setAttribute(name, value);
      });
    });
    const base = captureDocument.createElement("base");
    base.href = ownerDocument.baseURI;
    captureDocument.head.appendChild(base);
    const styles = Array.from(
      ownerDocument.querySelectorAll('style, link[rel="stylesheet"]')
    ).map((style) => {
      const clone = style.cloneNode(true);
      // Libraries such as MUI can insert rules through CSSOM, leaving the
      // style element's text empty. cloneNode alone loses those rules.
      if (style.tagName === "STYLE" && style.sheet) {
        try {
          clone.textContent = Array.from(style.sheet.cssRules)
            .map((rule) => rule.cssText)
            .join("\n");
        } catch {
          // Retain the cloned text if the browser denies stylesheet access.
        }
      }
      const loaded =
        clone.tagName === "LINK"
          ? new Promise((resolve, reject) => {
              clone.onload = resolve;
              clone.onerror = () =>
                reject(new Error("Report stylesheet failed to load"));
            })
          : Promise.resolve();
      captureDocument.head.appendChild(clone);
      return loaded;
    });

    const report = source.cloneNode(true);
    let tree = report;
    for (
      let ancestor = source.parentElement;
      ancestor && ancestor !== ownerDocument.body;
      ancestor = ancestor.parentElement
    ) {
      const parent = ancestor.cloneNode(false);
      parent.appendChild(tree);
      tree = parent;
    }
    captureDocument.body.appendChild(tree);
    await Promise.race([Promise.all(styles), cancelled]);
    if (signal.aborted) return false;

    const stillStyles = captureDocument.createElement("style");
    stillStyles.textContent =
      "*, *::before, *::after { transition: none !important; animation: none !important; }";
    captureDocument.head.appendChild(stillStyles);
    normalizeReportColors(captureDocument.documentElement);

    // html2canvas parses this option itself; it cannot resolve CSS var().
    const background = captureDocument.createElement("div");
    background.style.backgroundColor = "var(--surface)";
    report.appendChild(background);
    normalizeReportColors(background);
    const backgroundColor =
      captureWindow.getComputedStyle(background).backgroundColor;
    background.remove();

    const children = Array.from(report.children);
    if (children.length >= 2) {
      const firstPage = captureDocument.createElement("div");
      report.insertBefore(firstPage, children[0]);
      firstPage.append(children[0], children[1]);
    }

    const pages = Array.from(report.children);
    for (let index = 0; index < pages.length; index++) {
      if (signal.aborted) return false;
      const canvas = await html2canvas(pages[index], {
        useCORS: true,
        backgroundColor,
        scale: 2,
      });
      try {
        if (signal.aborted) return false;
        onPage(canvas, index);
      } finally {
        // Release the full-resolution pixel buffer after encoding each page.
        canvas.width = 0;
        canvas.height = 0;
      }
    }
    return true;
  } catch (error) {
    if (signal.aborted) return false;
    throw error;
  } finally {
    signal.removeEventListener("abort", dispose);
    dispose();
  }
}
